import {
  AssetsProvider,
  Image,
  Link,
  notDesktop,
  styled,
  Text,
} from '@obvio/app'
import { Grid, Stack } from '@obvio/ui'

import { BlogDateCategory } from '../BlogDateCategory'

import type { ArticleCategory } from './BlogIndex'
import type { DisplayType } from '../BlogDateCategory'
import type { ReactElement } from 'react'

const MockImgBlock = styled.div`
  background-color: ${(theme) => theme.colors.gray};
  width: 100%;
  height: 100%;
`

const ArticleStack = styled(Stack)<{ $type?: DisplayType | undefined }>`
  grid-column: ${(_, { $type }) => ($type === 'big' ? 'span 2' : 'span 1')};
  align-items: ${(_, { $type }) => ($type === 'big' ? 'center' : 'start')};
  margin-bottom: ${(theme) => theme.spacing.large};

  @media ${notDesktop} {
    grid-column: span 2;
    align-items: start;
  }
`

const Title = styled(Text)`
  font-family: ${(theme) => theme.font};
  font-weight: 300;
  font-size: 1.5rem;
  color: #1b1d1b;
`

const Description = styled(Text)<{ $type?: DisplayType | undefined }>`
  max-width: 34.625rem;
  text-align: ${(_, { $type }) => ($type === 'big' ? 'center' : 'start')};
  font-size: 1rem;
  font-weight: 300;

  @media ${notDesktop} {
    text-align: start;
  }
`

const ImgLink = styled(Link)<{ $type?: DisplayType | undefined }>`
  position: relative;
  width: 100%;

  height: ${(_, { $type }) => ($type === 'big' ? '32.3125rem' : '20.1875rem')};
  @media ${notDesktop} {
    height: 20.1875rem;
  }
`

export type ArticleProps = {
  article: ArticleCategory['articles'][0]
  type: 'big' | 'small'
}

function Article({ article, type }: ArticleProps) {
  const linkHref = `/blog/post/${article.slug}`
  return (
    <ArticleStack kind="vertical" $type={type}>
      <ImgLink href={linkHref} $type={type}>
        {article.image ? <Image img={article.image} /> : <MockImgBlock />}
      </ImgLink>
      <BlogDateCategory
        createdAt={article.createdAt}
        title={article.title}
        type={type}
      />
      <Link href={linkHref}>
        <Title tag="h3">{article.title}</Title>
      </Link>
      <Description $type={type}>{article.excerpt}</Description>
    </ArticleStack>
  )
}

type BlogIndexArticlesProps = {
  articles: ArticleCategory['articles']
}

export function BlogIndexArticles({
  articles,
}: BlogIndexArticlesProps): ReactElement {
  return (
    <Grid templateColumns="repeat(2, minmax(0,1fr))" gap="medium">
      {articles.map((article, i) => {
        const type: ArticleProps['type'] = i % 3 === 0 ? 'big' : 'small'

        return (
          <AssetsProvider key={article.id} path={`/article/${article.id}`}>
            <Article article={article} type={type} />
          </AssetsProvider>
        )
      })}
    </Grid>
  )
}
